"use client";

import * as React from "react";
import { Button } from "@/components/ui/button";
import { Message } from "@/lib/core/crm/types";
import { MessageActionHandlers } from "./hooks/useMessageActionHandlers";
import { ActionButton, ACTION_CONFIGS, type ActionButtonConfig } from "./ActionButton";
import { DiagnosticButton } from "@wtc/ui";
import { useCreateTask } from "@/lib/hooks/api/tasks";
import { CentralizedDrawer, useGlobalDrawerStack } from "@wtc/ui";
import { MoreHorizontal, Star, AlertTriangle, Printer, Download } from "lucide-react";

interface MessageActionsMenuProps {
  message: Message;
  handlers: MessageActionHandlers;
  onCopyLink?: () => void;
}

// Fully static configs (no props/state referenced) → hoisted to module scope so
// the reference is stable across renders (MEMO_STABLE_PROPS).
const REPLY_ACTION_CONFIG: ActionButtonConfig = {
  ...ACTION_CONFIGS.reply,
  variant: "ghost",
  showLabel: true,
  className: "w-full justify-start h-12",
};

const FORWARD_ACTION_CONFIG: ActionButtonConfig = {
  ...ACTION_CONFIGS.forward,
  variant: "ghost",
  showLabel: true,
  className: "w-full justify-start h-12",
};

const CREATE_TASK_ACTION_CONFIG: ActionButtonConfig = {
  ...ACTION_CONFIGS.createTask,
  variant: "ghost",
  showLabel: true,
  className: "w-full justify-start h-12",
};

const ARCHIVE_ACTION_CONFIG: ActionButtonConfig = {
  ...ACTION_CONFIGS.archive,
  showLabel: true,
  className: "w-full justify-start h-12",
};

const DELETE_ACTION_CONFIG: ActionButtonConfig = {
  ...ACTION_CONFIGS.delete,
  variant: "ghost",
  showLabel: true,
  className: "w-full justify-start h-12 text-destructive hover:text-destructive",
};

export function MessageActionsMenu({ message, handlers, onCopyLink }: MessageActionsMenuProps) {
  const { openDrawer, closeDrawer, isDrawerOpen } = useGlobalDrawerStack();
  const open = isDrawerOpen("mobile-message-actions");
  const { mutateAsync: createTask } = useCreateTask();

  const handleActionAndClose = React.useCallback(
    (action: () => void) => {
      action();
      closeDrawer("mobile-message-actions");
    },
    [closeDrawer]
  );

  // Only render on mobile - prevent desktop drawer
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640); // sm breakpoint
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Stable prop references so ActionButton's memoization holds (MEMO_STABLE_PROPS).
  // Config depends on message.isRead; handler objects close over handlers + handleActionAndClose.
  const statusActionConfig = React.useMemo<ActionButtonConfig>(
    () => ({
      ...(message.isRead ? ACTION_CONFIGS.markUnread : ACTION_CONFIGS.markRead),
      variant: "ghost",
      showLabel: true,
      className: "w-full justify-start h-12",
    }),
    [message.isRead]
  );

  const replyHandlers = React.useMemo<MessageActionHandlers>(
    () => ({
      ...handlers,
      handleReply: () => handleActionAndClose(handlers.handleReply),
    }),
    [handlers, handleActionAndClose]
  );

  const forwardHandlers = React.useMemo<MessageActionHandlers>(
    () => ({
      ...handlers,
      handleForward: () => handleActionAndClose(handlers.handleForward),
    }),
    [handlers, handleActionAndClose]
  );

  const createTaskHandlers = React.useMemo<MessageActionHandlers>(
    () => ({
      ...handlers,
      handleCreateTask: () => handleActionAndClose(handlers.handleCreateTask),
    }),
    [handlers, handleActionAndClose]
  );

  const archiveHandlers = React.useMemo<MessageActionHandlers>(
    () => ({
      ...handlers,
      handleArchive: () => handleActionAndClose(handlers.handleArchive),
    }),
    [handlers, handleActionAndClose]
  );

  const statusHandlers = React.useMemo<MessageActionHandlers>(
    () => ({
      ...handlers,
      handleMarkRead: () => handleActionAndClose(handlers.handleMarkRead),
      handleMarkUnread: () => handleActionAndClose(handlers.handleMarkUnread),
    }),
    [handlers, handleActionAndClose]
  );

  const deleteHandlers = React.useMemo<MessageActionHandlers>(
    () => ({
      ...handlers,
      handleDelete: () => handleActionAndClose(handlers.handleDelete),
    }),
    [handlers, handleActionAndClose]
  );

  // Don't render drawer functionality on desktop
  if (!isMobile) {
    return null;
  }

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => openDrawer("mobile-message-actions")}
        title="More actions"
        disabled={handlers.isActionLoading}
      >
        <MoreHorizontal className="h-4 w-4" />
      </Button>

      <CentralizedDrawer
        drawerId="mobile-message-actions"
        side="bottom"
        height="70vh"
        title="Message Actions"
        description={`Actions for: ${message.subject}`}
        showCloseButton={true}
        enableSwipeGesture={true}
      >
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-4">
            {/* Primary Actions */}
            <div className="space-y-2">
              <h3 className="font-medium text-card-foreground">Primary Actions</h3>
              <p className="text-sm text-gray-500">Common message operations</p>
              <ActionButton config={REPLY_ACTION_CONFIG} handlers={replyHandlers} />

              <ActionButton config={FORWARD_ACTION_CONFIG} handlers={forwardHandlers} />

              <ActionButton config={CREATE_TASK_ACTION_CONFIG} handlers={createTaskHandlers} />

              <ActionButton config={ARCHIVE_ACTION_CONFIG} handlers={archiveHandlers} />
            </div>

            {/* Message Status */}
            <div className="space-y-2">
              <h3 className="font-medium text-card-foreground">Message Status</h3>
              <p className="text-sm text-gray-500">Change message read status and importance</p>
              <ActionButton config={statusActionConfig} handlers={statusHandlers} />

              <Button
                variant="ghost"
                className="w-full justify-start h-12"
                onClick={() => handleActionAndClose(handlers.handleMarkImportant)}
                disabled={handlers.isActionLoading}
              >
                <Star className="h-4 w-4 mr-3" />
                Mark as Important
              </Button>
            </div>

            {/* Export & Print */}
            <div className="space-y-2">
              <h3 className="font-medium text-card-foreground">Export & Print</h3>
              <p className="text-sm text-gray-500">Print message content</p>
              <Button
                variant="ghost"
                className="w-full justify-start h-12"
                onClick={() => handleActionAndClose(handlers.handlePrint)}
                disabled={handlers.isActionLoading}
              >
                <Printer className="h-4 w-4 mr-3" />
                Print
              </Button>

              {onCopyLink && (
                <Button
                  variant="ghost"
                  className="w-full justify-start h-12"
                  onClick={() => handleActionAndClose(onCopyLink)}
                  disabled={handlers.isActionLoading}
                >
                  <Download className="h-4 w-4 mr-3" />
                  Copy Link
                </Button>
              )}
            </div>

            {/* Dangerous Actions */}
            <div className="space-y-2">
              <h3 className="font-medium text-card-foreground">Dangerous Actions</h3>
              <p className="text-sm text-gray-500">Destructive operations - use with caution</p>
              <Button
                variant="ghost"
                className="w-full justify-start h-12 text-destructive hover:text-destructive"
                onClick={() => handleActionAndClose(handlers.handleMarkSpam)}
                disabled={handlers.isActionLoading}
              >
                <AlertTriangle className="h-4 w-4 mr-3" />
                Mark as Spam
              </Button>

              <ActionButton config={DELETE_ACTION_CONFIG} handlers={deleteHandlers} />
            </div>
          </div>
        </div>
      </CentralizedDrawer>
    </>
  );
}
